import { IconButton } from '@ui/shared/Button';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { IndexRange } from 'react-virtualized';
import { LIST_ROW_HEIGHTS } from '../../../constants/list_row_heights';
import {
  closePopup,
  openPopup,
  popup,
} from '../../../data/providers/popupsProvider';
import { Entity, EntityType } from '../../../data/types';
import { useInfiniteSessions } from '../../../hooks/sessions/useInfiniteSessions';
import { fullDate } from '../../../utils/date';
import { Column } from '../../layout/Column';
import { ScrollableArea } from '../../layout/ScrollableArea';
import { ListControlLabel } from '../../shared/form/ListControlLabel';
import { InfiniteList } from '../../shared/List/InfiniteList';
import { ListControlContainer } from '../../shared/List/ListControlContainer';
import { ListItem } from '../../shared/List/ListItem';
import { LinearLoading } from '../../shared/loading/LinearLoading';
import { NoResults } from '../../shared/NoResults';
import { SessionExportPopup } from './SessionExportPopup';
import { SessionPopup } from './SessionPopup';

interface IProps extends RouteComponentProps {
  style?: React.CSSProperties;
  entityType: EntityType;
  entity: Entity;
}
export const SessionsCard = withRouter((props: IProps): React.ReactElement => {
  const { sessionsLoading, setStartAfter, sessions, setEntity } =
    useInfiniteSessions({
      entityId: props.entity.id,
      entityType: props.entityType,
    });

  React.useEffect((): void => {
    setEntity({ entityId: props?.entity?.id, entityType: props.entityType });
  }, [props?.entity?.id, props?.entityType]);

  /**
   * @description handler function for when the user closes the download dialog
   */
  const onCloseDialog = (): void => {
    popup.next(null);
  };

  /**
   * @description handler function for when the user chooses to download a session
   * @param id {string} the session id
   */
  const onDownload = (
    id: number
  ): ((e: React.MouseEvent<HTMLElement>) => void) => {
    return async (e: React.MouseEvent<HTMLElement>): Promise<void> => {
      e.stopPropagation();

      popup.next(
        <SessionExportPopup
          closeDialog={onCloseDialog}
          entity={props.entity}
          entityType={props.entityType}
          sessionId={id}
        />
      );
    };
  };

  /**
   *
   * @param param0
   */
  const rowRenderer = ({
    index,
    key,
    style,
  }: {
    index: number;
    key: string;
    style: React.CSSProperties;
  }): React.ReactElement => {
    const date = +sessions[index].id;
    const onClickHandler = (): void => {
      openPopup(
        <SessionPopup
          sessionId={date}
          onClose={closePopup(props.history)}
          entity={props.entity}
          entityType={props.entityType}
        />,
        props.history,
        `session-${date}`
      );
    };
    const { count } = sessions[index];
    return (
      // eslint-disable-next-line react/jsx-no-bind
      <ListItem style={style} key={key} onClick={onClickHandler}>
        <Column role='cell' style={{ flex: 1 }}>
          <div>{fullDate(date)}</div>
          <div style={{ color: 'var(--color-secondary-text)' }}>{`${count} ${
            count > 1 ? 'Tests' : 'Test'
          }`}</div>
        </Column>
        <div role='cell'>
          <IconButton
            icon='file_download'
            onClick={onDownload(date)}
            title='Download Session'
          />
        </div>
      </ListItem>
    );
  };

  const fetchNext = ({ startIndex }: IndexRange): Promise<void> => {
    if (!(sessions.length > 0)) return;
    const lastTest = +sessions[startIndex - 1].id;
    setStartAfter(lastTest);
    return Promise.resolve();
  };

  return (
    <React.Fragment>
      <ListControlLabel label='Sessions'></ListControlLabel>
      <ListControlContainer>
        <ScrollableArea
          style={{
            flex: 1,
            overflowY: 'hidden',
          }}
        >
          {sessions && sessions.length > 0 ? (
            <InfiniteList
              data={sessions}
              rowHeight={LIST_ROW_HEIGHTS.normal}
              rowRenderer={rowRenderer}
              onMore={fetchNext}
            />
          ) : (
            !sessionsLoading && <NoResults message='No Testing Sessions' />
          )}
          {sessionsLoading && <LinearLoading style={{ marginTop: 'auto' }} />}
        </ScrollableArea>
      </ListControlContainer>
    </React.Fragment>
  );
});
